import React from 'react';
import { useTranslation } from 'react-i18next';
import { RoleConflictView, RoleFitCandidatesView } from '../../../api/General/businessRoles';
import {
  Dialog, DialogContent, DialogHeader, DialogBody, DialogFooter,
  DialogTitle, DialogDescription,
} from '../../../components/ui/Dialog';
import Button from '../../../components/ui/Button';

interface RoleConflictModalProps {
  open: boolean;
  conflicts: RoleConflictView[];
  onProceed: () => void;
  onCancel: () => void;
  // Poor-fit advisory (Q3). Optional so conflict-only opens are unaffected.
  fitCandidates?: RoleFitCandidatesView | null;
  fitPercent?: number;
}

export default function RoleConflictModal({
  open,
  conflicts,
  onProceed,
  onCancel,
  fitCandidates,
  fitPercent,
}: RoleConflictModalProps) {
  const { t } = useTranslation();

  // Only real conflicts can hide Proceed. A poor fit is advisory and arrives via
  // fitPercent — it is never synthesized into `conflicts`, so it cannot block HR.
  const hasErrors = conflicts.some(c => c.severity === 'error');

  // Opened for a poor fit alone: the heading must say so rather than claim a conflict.
  const fitOnly = fitPercent !== undefined && conflicts.length === 0;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onCancel(); }}>
      <DialogContent size="default">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-warning/10 rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-warning" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div>
              <DialogTitle>{fitOnly ? t('roleFit.reviewTitle') : t('businessRoles.conflictDetected')}</DialogTitle>
              <DialogDescription>{fitOnly ? t('roleFit.reviewDescription') : t('businessRoles.conflictDescription')}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <DialogBody className="space-y-3 max-h-80 overflow-y-auto">
          {conflicts.map((conflict, index) => (
            <div
              key={index}
              className={`rounded-lg p-3 border ${
                conflict.severity === 'error'
                  ? 'bg-destructive/5 border-destructive/20'
                  : 'bg-warning/5 border-warning/20'
              }`}
            >
              <div className="flex items-start gap-2">
                {conflict.severity === 'error' ? (
                  <svg className="w-4 h-4 text-destructive mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4 text-warning mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                )}
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${
                    conflict.severity === 'error' ? 'text-destructive' : 'text-warning'
                  }`}>
                    {conflict.message}
                  </p>
                  {conflict.existingRoleName && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {t('businessRoles.existingRole')}: {conflict.existingRoleName}
                    </p>
                  )}
                  {conflict.overlappingResponsibilities.length > 0 && (
                    <div className="mt-2">
                      <p className="text-xs text-muted-foreground mb-1">{t('businessRoles.overlappingResponsibilities')}:</p>
                      <div className="flex flex-wrap gap-1">
                        {conflict.overlappingResponsibilities.map((resp, i) => (
                          <span key={i} className="inline-flex px-1.5 py-0.5 bg-card/60 rounded text-xs text-foreground">{resp}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}

          {fitPercent !== undefined && (
            <div data-testid="fit-warning" className="rounded-lg p-3 border bg-warning/5 border-warning/20">
              <div className="flex items-start gap-2">
                <svg className="w-4 h-4 text-warning mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-warning">
                    {t('roleFit.warnTitle', { percent: fitPercent })}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">{t('roleFit.warnBody')}</p>

                  {fitCandidates && fitCandidates.candidates.length > 0 && (
                    <div className="mt-3">
                      {/* A named department must not be misread as company-wide, and a company-wide
                          pool must not render a heading that trails off with no department. */}
                      <p className="text-xs font-medium text-foreground mb-1">
                        {fitCandidates.departmentName
                          ? t('roleFit.betterFitsIn', { department: fitCandidates.departmentName })
                          : t('roleFit.betterFitsCompanyWide')}
                      </p>
                      <ul className="space-y-1">
                        {fitCandidates.candidates.slice(0, 5).map((c) => (
                          <li
                            key={c.employeeId}
                            data-testid={`fit-candidate-${c.employeeId}`}
                            className="flex flex-wrap items-center gap-x-2 text-xs"
                          >
                            <span className="font-medium text-foreground">{c.employeeName || t('UnknownUser')}</span>
                            <span className="text-muted-foreground">
                              {t('roleFit.candidateSummary', {
                                percent: Math.round(c.coveragePercent),
                                strong: c.strongCount,
                                developing: c.developingCount,
                                missing: c.missingCount,
                              })}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="secondary" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
          {!hasErrors && (
            <Button variant="warning" onClick={onProceed}>
              {t('businessRoles.proceedAnyway')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
